import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import StaticSeo from '../components/StaticSeo.jsx';

// ─────────────────────────────────────────────────────────────────────────
// THE STREAK DIAGNOSTIC
// Window Washing Guide — Tool 01 of 09  · SIGNATURE
// ─────────────────────────────────────────────────────────────────────────
//
// Visual pattern-matching quiz that diagnoses what's actually wrong with a
// dirty/hazy/streaking window. Output: the correct diagnosis + the article
// that covers it in depth + the recommended fix protocol.
//
// Diagnostic flow:
//   Q1 — WHERE is the problem? (location funnel)
//   Q2 — WHAT does it look like? (visual archetype matching, 4 options per
//        path, illustrated with inline SVG)
//   Q3 — One disambiguating follow-up (varies by branch)
//   → Verdict: matched diagnosis + linked deep-dive article
//
// Twelve diagnoses possible (matched 1:1 to existing Diagnostician articles):
//   • surface-residue ......... → Article 001 (why-windows-look-worse)
//   • white-rain-spots ........ → Article 005 (white-spots-after-rain)
//   • failed-igu-seal ......... → Article 006 (foggy-windows-failed-seal)
//   • outgassing-film ......... → Article 007 (rainbow-oily-film-glass)
//   • fabricating-debris ...... → Article 008 (scratches-after-cleaning)
//   • surfactant-hysteresis ... → Article 009 (streaks-come-back-overnight)
//   • gasket-mold ............. → Article 010 (black-mold-windows-frames-glass)
//   • frame-rot ............... → Article 010 (black-mold-windows-frames-glass)
//   • butyl-migration ......... → Article 010 (black-mold-windows-frames-glass)
//   • stubborn-organic ........ → Article 011 (solvent-ladder-sap-tar-bugs)
//   • etching ................. → Article 012 (hard-water-etching-vs-deposits)
//   • pressure-wash-haze ...... → Article 013 (cloudy-windows-after-pressure-washing)
//
// Brand: matches TintIdentifier exactly — same T tokens, same fonts,
// same animation primitives, same dark mode toggle, same numbered header.
//
// ─────────────────────────────────────────────────────────────────────────

// ─── visual archetypes (inline SVG) ───────────────────────────────────────
// Each archetype is a 200×140 SVG that schematically illustrates a streak
// or haze pattern. Designed to be recognizable from a thumbnail.

function VisualUniformHaze({ T }) {
  return (
    <svg viewBox="0 0 200 140" width="100%" height="100%">
      <rect x="6" y="6" width="188" height="128" fill={T.surface} stroke={T.rule} />
      <rect x="6" y="6" width="188" height="128" fill={T.inkFaint} opacity="0.35" />
      <line x1="6" y1="36" x2="194" y2="36" stroke={T.rule} opacity="0.4" strokeWidth="0.5" />
      <line x1="6" y1="70" x2="194" y2="70" stroke={T.rule} opacity="0.4" strokeWidth="0.5" />
      <line x1="6" y1="104" x2="194" y2="104" stroke={T.rule} opacity="0.4" strokeWidth="0.5" />
    </svg>
  );
}

function VisualDirectionalStreaks({ T }) {
  return (
    <svg viewBox="0 0 200 140" width="100%" height="100%">
      <rect x="6" y="6" width="188" height="128" fill={T.surface} stroke={T.rule} />
      {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
        <line
          key={i}
          x1={20 + i * 22}
          y1="14"
          x2={50 + i * 22}
          y2="130"
          stroke={T.inkFaint}
          opacity="0.55"
          strokeWidth="3"
        />
      ))}
    </svg>
  );
}

function VisualRainbowSheen({ T }) {
  return (
    <svg viewBox="0 0 200 140" width="100%" height="100%">
      <defs>
        <linearGradient id="rainbow" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#9CA89A" stopOpacity="0.5" />
          <stop offset="33%" stopColor="#B5532A" stopOpacity="0.4" />
          <stop offset="66%" stopColor="#9CA89A" stopOpacity="0.5" />
          <stop offset="100%" stopColor="#A39E91" stopOpacity="0.6" />
        </linearGradient>
      </defs>
      <rect x="6" y="6" width="188" height="128" fill={T.surface} stroke={T.rule} />
      <rect x="6" y="6" width="188" height="128" fill="url(#rainbow)" />
    </svg>
  );
}

function VisualWhiteSpots({ T }) {
  return (
    <svg viewBox="0 0 200 140" width="100%" height="100%">
      <rect x="6" y="6" width="188" height="128" fill={T.surface} stroke={T.rule} />
      {[
        [30, 30], [55, 24], [80, 36], [110, 28], [140, 40], [170, 32],
        [40, 60], [70, 70], [100, 60], [130, 75], [160, 65],
        [25, 95], [55, 105], [85, 95], [115, 110], [145, 100], [175, 108],
      ].map(([x, y], i) => (
        <circle
          key={i}
          cx={x}
          cy={y}
          r={1.5 + (i % 3) * 0.8}
          fill={T.ink}
          opacity="0.35"
        />
      ))}
    </svg>
  );
}

function VisualBetweenPaneFog({ T }) {
  return (
    <svg viewBox="0 0 200 140" width="100%" height="100%">
      <defs>
        <radialGradient id="fog" cx="0.3" cy="0.85" r="0.85">
          <stop offset="0%" stopColor="#A39E91" stopOpacity="0.85" />
          <stop offset="60%" stopColor="#A39E91" stopOpacity="0.3" />
          <stop offset="100%" stopColor="#A39E91" stopOpacity="0" />
        </radialGradient>
      </defs>
      <rect x="6" y="6" width="188" height="128" fill={T.surface} stroke={T.rule} />
      <rect x="6" y="6" width="188" height="128" fill="url(#fog)" />
      <rect x="6" y="6" width="188" height="128" fill="none" stroke={T.rule} strokeDasharray="2 4" />
    </svg>
  );
}

function VisualEdgeBand({ T }) {
  return (
    <svg viewBox="0 0 200 140" width="100%" height="100%">
      <rect x="6" y="6" width="188" height="128" fill={T.surface} stroke={T.rule} />
      <rect x="6" y="6" width="188" height="14" fill={T.ink} opacity="0.45" />
      <rect x="6" y="120" width="188" height="14" fill={T.ink} opacity="0.45" />
      <rect x="6" y="6" width="14" height="128" fill={T.ink} opacity="0.45" />
      <rect x="180" y="6" width="14" height="128" fill={T.ink} opacity="0.45" />
    </svg>
  );
}

function VisualScratches({ T }) {
  return (
    <svg viewBox="0 0 200 140" width="100%" height="100%">
      <rect x="6" y="6" width="188" height="128" fill={T.surface} stroke={T.rule} />
      {[
        [25, 30, 75, 30],
        [40, 50, 90, 52],
        [30, 70, 80, 71],
        [50, 90, 100, 91],
        [70, 110, 120, 111],
        [110, 35, 160, 36],
        [120, 55, 170, 56],
        [105, 75, 155, 76],
        [125, 95, 175, 96],
      ].map(([x1, y1, x2, y2], i) => (
        <g key={i}>
          <circle cx={x1} cy={y1} r="1.5" fill={T.ink} opacity="0.6" />
          <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={T.ink} opacity="0.4" strokeWidth="0.6" />
        </g>
      ))}
    </svg>
  );
}

function VisualBlackDots({ T }) {
  return (
    <svg viewBox="0 0 200 140" width="100%" height="100%">
      <rect x="6" y="6" width="188" height="128" fill={T.surface} stroke={T.rule} />
      {/* glass clean center */}
      {/* gasket band at bottom with mold dots */}
      <rect x="6" y="115" width="188" height="19" fill="#3A3A36" />
      {[14, 26, 38, 52, 67, 79, 92, 108, 122, 138, 154, 170, 184].map((x, i) => (
        <g key={i}>
          <circle cx={x} cy={120 + (i % 3) * 4} r={1.5 + (i % 2) * 0.8} fill="#0a0a09" />
          <circle cx={x + 3} cy={126 - (i % 2) * 3} r="1" fill="#0a0a09" opacity="0.7" />
        </g>
      ))}
    </svg>
  );
}

function VisualSapDrops({ T }) {
  return (
    <svg viewBox="0 0 200 140" width="100%" height="100%">
      <rect x="6" y="6" width="188" height="128" fill={T.surface} stroke={T.rule} />
      {[
        [40, 30, 4], [85, 45, 6], [130, 35, 5], [165, 55, 4.5],
        [55, 70, 7], [100, 80, 5.5], [140, 90, 6], [60, 105, 5],
        [115, 110, 4.5], [155, 115, 5.5],
      ].map(([cx, cy, r], i) => (
        <g key={i}>
          <ellipse cx={cx} cy={cy} rx={r} ry={r * 1.3} fill={T.sienna} opacity="0.55" />
          <ellipse cx={cx - r * 0.3} cy={cy - r * 0.3} rx={r * 0.3} ry={r * 0.4} fill="#fff" opacity="0.4" />
        </g>
      ))}
    </svg>
  );
}

function VisualUniformMilky({ T }) {
  return (
    <svg viewBox="0 0 200 140" width="100%" height="100%">
      <rect x="6" y="6" width="188" height="128" fill={T.surface} stroke={T.rule} />
      <rect x="6" y="6" width="188" height="128" fill="#E8E5DD" opacity="0.85" />
    </svg>
  );
}

function VisualEtchedHaze({ T }) {
  return (
    <svg viewBox="0 0 200 140" width="100%" height="100%">
      <rect x="6" y="6" width="188" height="128" fill={T.surface} stroke={T.rule} />
      <defs>
        <pattern id="etch" patternUnits="userSpaceOnUse" width="6" height="6">
          <circle cx="3" cy="3" r="0.6" fill={T.inkFaint} opacity="0.4" />
        </pattern>
      </defs>
      <rect x="6" y="6" width="188" height="128" fill="url(#etch)" />
    </svg>
  );
}

function VisualPressureWash({ T }) {
  return (
    <svg viewBox="0 0 200 140" width="100%" height="100%">
      <defs>
        <linearGradient id="pwash" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#A39E91" stopOpacity="0.6" />
          <stop offset="100%" stopColor="#A39E91" stopOpacity="0.85" />
        </linearGradient>
      </defs>
      <rect x="6" y="6" width="188" height="128" fill={T.surface} stroke={T.rule} />
      <rect x="6" y="6" width="188" height="128" fill="url(#pwash)" />
      {/* faint vertical runoff streaks */}
      {[40, 80, 120, 160].map((x, i) => (
        <line key={i} x1={x} y1="10" x2={x + 4} y2="130" stroke={T.ink} opacity="0.15" strokeWidth="1.5" />
      ))}
    </svg>
  );
}

// ─── decision tree ────────────────────────────────────────────────────────

const Q1 = {
  id: 'location',
  title: 'Where is the problem on your window?',
  italic: 'the location is the single biggest diagnostic clue',
  note: 'The first split: surface haze vs. between-pane fog vs. edge-specific damage. Most homeowners skip this question and go straight to "what cleaner do I need?" — which is why most homeowners reach for the wrong cleaner.',
  options: [
    {
      id: 'surface',
      label: 'Surface haze, streaks, or film across the glass',
      desc: 'Whatever it is, it covers the visible glass area. Usually wipes off when wet, comes back when dry.',
    },
    {
      id: 'between',
      label: 'Inside the pane — I cannot wipe it off either side',
      desc: 'Cleaning the interior and exterior surfaces does nothing. The haze is sealed inside the unit.',
    },
    {
      id: 'spots',
      label: 'Discrete spots, dots, or droplet marks',
      desc: 'Not a continuous haze. Distinct individual spots, often clustered.',
    },
    {
      id: 'edges',
      label: 'Only at the edges, gasket, or frame area',
      desc: 'Center of the glass is clear. The problem is concentrated where glass meets frame.',
    },
    {
      id: 'damage',
      label: 'Scratches, gouges, or visible mechanical damage',
      desc: 'Not chemical or biological — actual lines or marks in or on the glass.',
    },
  ],
};

// Q2 branches — each Q1 option leads to a different visual matching question.
const Q2_BRANCHES = {
  surface: {
    id: 'surface-pattern',
    title: 'Which pattern best matches what you see?',
    italic: 'pick the closest match — the visual archetype is the diagnosis',
    note: 'Surface haze divides into four distinct archetypes. The patterns look subtle in person but are unmistakable once you know what to look for.',
    options: [
      {
        id: 'directional',
        label: 'Streaks running in the direction of your last wipe',
        desc: 'Faint diagonal or horizontal lines that follow your cleaning motion. Often worse in morning light.',
        Visual: VisualDirectionalStreaks,
      },
      {
        id: 'rainbow',
        label: 'Iridescent or oily-looking sheen',
        desc: 'A faint rainbow cast at certain angles. Looks like the surface of a soap bubble.',
        Visual: VisualRainbowSheen,
      },
      {
        id: 'milky',
        label: 'Uniform milky/cloudy haze across the whole pane',
        desc: 'Even, opaque haze. Same intensity everywhere. Happened recently, possibly after a service.',
        Visual: VisualUniformMilky,
      },
      {
        id: 'etched',
        label: 'Subtle uniform haziness with a sandblasted feel',
        desc: 'Glass looks slightly frosted. Does not respond to any cleaner. Has been there for months or years.',
        Visual: VisualEtchedHaze,
      },
    ],
  },
  between: {
    id: 'between-pattern',
    title: 'How is the inside-the-pane fog distributed?',
    italic: 'between-pane fog has a characteristic geometry',
    note: "Failed insulated glass units show fog in specific patterns. The pattern tells you how advanced the failure is and what your replacement options look like.",
    options: [
      {
        id: 'corner',
        label: 'Concentrated at one corner or edge',
        desc: 'Worst in one specific area — often the bottom or a single corner. Fades toward the rest of the pane.',
        Visual: VisualBetweenPaneFog,
      },
      {
        id: 'uniform',
        label: 'Even, milky haze across the whole inside',
        desc: 'Uniform cloudiness throughout. Often advanced — the desiccant is fully saturated.',
        Visual: VisualUniformMilky,
      },
      {
        id: 'intermittent',
        label: 'Comes and goes with the weather',
        desc: 'Foggy on cold mornings, clear by afternoon. Early-stage seal failure.',
        Visual: VisualBetweenPaneFog,
      },
    ],
  },
  spots: {
    id: 'spots-pattern',
    title: 'What do the spots look like?',
    italic: 'the spot pattern points directly at the source',
    note: 'Spots come from upstream sources — sprinklers, roof runoff, masonry, specific contaminants. The spot pattern itself usually tells you where to look.',
    options: [
      {
        id: 'chalky-white',
        label: 'Chalky white droplet marks',
        desc: 'Discrete white spots, often clustered. Visible mostly on the exterior. Often appears after rain or sprinkler use.',
        Visual: VisualWhiteSpots,
      },
      {
        id: 'gritty',
        label: 'Gritty deposits with slight texture',
        desc: 'White or gray spots that feel slightly sandy when rubbed. Often after a recent roof or shingle event.',
        Visual: VisualWhiteSpots,
      },
      {
        id: 'sap-amber',
        label: 'Amber, sticky, tar-like droplets',
        desc: 'Yellow-brown drops, possibly tacky to the touch. Often near trees or after roadwork.',
        Visual: VisualSapDrops,
      },
    ],
  },
  edges: {
    id: 'edges-pattern',
    title: 'What does the edge problem look like?',
    italic: 'the edge zone is where three different problems converge',
    note: "Edge issues are commonly misdiagnosed because three distinct problems all appear in the same physical location: gasket mold, frame rot, and butyl migration. The visual signature distinguishes them.",
    options: [
      {
        id: 'mold-dots',
        label: 'Black or dark dots on the rubber gasket',
        desc: 'Small fuzzy spots clustered along the gasket. Concentrated at the bottom inch.',
        Visual: VisualBlackDots,
      },
      {
        id: 'wood-rot',
        label: 'Dark patches on the wood frame, possibly soft',
        desc: 'Staining on the wood itself, following the grain. Wood may dent when pressed.',
        Visual: VisualBlackDots,
      },
      {
        id: 'edge-band',
        label: 'A dark band on the glass at the edge perimeter',
        desc: 'A thin, uniform dark line running along where the glass meets the frame. Slightly tacky.',
        Visual: VisualEdgeBand,
      },
      {
        id: 'pressure-edge',
        label: 'Edge haze that appeared after a pressure washing service',
        desc: 'Cloudy or hazy band at the edges that was not there before recent exterior service work.',
        Visual: VisualPressureWash,
      },
    ],
  },
  damage: {
    id: 'damage-pattern',
    title: 'What does the damage look like?',
    italic: 'mechanical damage to glass has specific signatures',
    note: 'True damage to the glass surface — as opposed to deposits or films — has a specific visual character that distinguishes it from cleaning problems.',
    options: [
      {
        id: 'comet',
        label: 'Many parallel hairline scratches in one direction',
        desc: 'Dozens of fine scratches, all running the same way. Often appeared after a single cleaning. Each scratch has a tiny pit at one end.',
        Visual: VisualScratches,
      },
      {
        id: 'random-scratch',
        label: 'A few isolated scratches in random directions',
        desc: 'One to several scratches, not all aligned. Likely from a specific incident.',
        Visual: VisualScratches,
      },
    ],
  },
};

// ─── verdicts ─────────────────────────────────────────────────────────────

const VERDICTS = {
  'surfactant-hysteresis': {
    name: 'Surfactant Residue Reactivation',
    italic: 'soap residue rehydrating overnight',
    rating: 'Common · Easily Fixable',
    summary:
      "What you're looking at is the residue left behind by your previous cleaning, becoming visible because overnight humidity rehydrated it. The cleaner pulled water out of the air, softened, and redistributed itself into the streak pattern you now see. This is one of the most common diagnoses we see, and the fix takes two minutes.",
    fix: [
      ['Strip the residue with a 50/50 alcohol pass', '50/50 isopropyl alcohol and distilled water, on a fresh microfiber. Do not reuse the cloth from your last clean.'],
      ['Dry-buff with a second clean microfiber', 'Single direction. Light pressure.'],
      ['Switch to a less surfactant-heavy cleaning protocol going forward', 'Or commit to the alcohol neutralization pass after every wash.'],
    ],
    avoid: [
      ['Switching cleaners and hoping it fixes itself', 'All consumer glass cleaners contain surfactants. The new cleaner just deposits new residue.'],
      ['Using paper towels for the alcohol pass', 'They leave fibers and reintroduce contamination.'],
    ],
    article: { title: 'The streaks that come back overnight', slug: 'streaks-come-back-overnight', num: '009' },
  },
  'outgassing-film': {
    name: 'VOC Outgassing Condensation',
    italic: 'your house is breathing chemistry onto its glass',
    rating: 'Common in Newer Homes · Patience Required',
    summary:
      "The iridescent sheen is volatile organic compound condensation — chemicals from new building materials, finishes, or furnishings vaporizing into your air and depositing on the coldest interior surfaces, which are usually your windows. It's most common in homes under five years old, but plug-in air fresheners, recent renovations, and new furniture can produce it in older homes.",
    fix: [
      ['Wipe with 91% isopropyl alcohol on a clean microfiber', "This dissolves the film immediately."],
      ['Follow with Sprayway Glass Cleaner #50 if alcohol falls short', 'Higher polar-solvent content than typical glass cleaners.'],
      ['Increase ventilation', 'Run HRV/ERV systems on high. Open windows when humidity allows. Speeds the underlying outgassing process.'],
    ],
    avoid: [
      ['Vinegar or acidic cleaners', 'Wrong chemistry entirely. Acid does not break the molecular bonds holding plasticizers to glass.'],
      ['Acetone or other harsh solvents', 'Alcohol does the job. Stronger solvents risk damaging gaskets and coatings.'],
    ],
    article: { title: 'The rainbow film on your glass', slug: 'rainbow-oily-film-glass', num: '007' },
  },
  'pressure-wash-haze': {
    name: 'Detergent Residue from Pressure Washing',
    italic: 'runoff from siding service, dried on your glass',
    rating: 'Recent Service · Cleanable',
    summary:
      "If a uniform milky haze appeared on your glass right after a pressure-washing service, the diagnosis is detergent runoff. The cleaner used a higher-concentration siding detergent, did not rinse the glass thoroughly afterward, and the residue dried in place. The cleanup takes about an hour for a typical residential elevation.",
    fix: [
      ['Wash with hot water + one drop of Dawn per quart', 'The surfactants in dish soap actively dissolve other dried surfactant residue.'],
      ['Rinse with clean water, squeegee dry', 'Standard finishing technique.'],
      ['Final alcohol neutralization pass', '50/50 isopropyl alcohol and distilled water on a fresh microfiber. Locks in the clean.'],
    ],
    avoid: [
      ['Vinegar or acid cleaners', 'Wrong chemistry. Surfactant residue needs surfactant-based cleaning.'],
      ['Hiring the same pressure-washing contractor again without a clear conversation', "If they damaged your windows once, they will do it again. Ask about their masking and rinse protocol before the next contract."],
    ],
    article: { title: 'Cloudy windows after pressure washing', slug: 'cloudy-windows-after-pressure-washing', num: '013' },
  },
  'etching': {
    name: 'Hard Water Etching',
    italic: 'permanent — chemistry has crossed a line',
    rating: 'PERMANENT · Restoration Required',
    summary:
      "The uniform haziness you're describing is etching — calcium ions from hard water have integrated into the silica matrix of the glass itself. This is no longer a deposit on the glass; it is a chemical modification of the glass. No cleaner will remove it. The fix is mechanical polishing with cerium oxide, or replacement.",
    fix: [
      ['Run the 30-second vinegar test to confirm', 'Soak a cloth in vinegar, hold against the glass for 30 seconds. If it lightens, you have removable deposits, not etching. If unchanged, the diagnosis stands.'],
      ['Cerium oxide polishing for small areas', 'Possible DIY for non-coated annealed glass under 1 sq ft. Otherwise call a glass restoration specialist.'],
      ['Replacement for severely etched or coated glass', "$250-600 installed for residential single-pane tempered."],
      ['Address the source to prevent recurrence', 'Sprinkler aim, water softener, hydrophobic glass treatment.'],
    ],
    avoid: [
      ['Continuing to scrub with vinegar or CLR', 'Will not work and may damage adjacent surfaces (low-E coatings, stone trim, gaskets).'],
      ['Magic Eraser', 'Will damage low-E coatings and not actually fix the etching.'],
    ],
    article: { title: 'Hard water etching vs. surface deposits', slug: 'hard-water-etching-vs-deposits', num: '012' },
  },
  'failed-igu-seal': {
    name: 'Failed IGU Seal',
    italic: 'manufactured component failure',
    rating: 'PERMANENT · Replacement Required',
    summary:
      "The fog you cannot wipe off either side is sealed inside the insulated glass unit. The hermetic seal has failed, the desiccant is saturated, and ambient humidity is now condensing inside the cavity. No cleaner will ever reach the affected surface. Your options are: replace the IGU, replace the whole window, attempt defogging (controversial), or do nothing.",
    fix: [
      ['Confirm with the alcohol test', 'Wipe both faces of the glass vigorously with isopropyl alcohol. If the haze remains visible from both sides, the moisture is between the panes.'],
      ['Get IGU replacement quotes', "$200-450 per unit for residential. Frame and sash stay; only the glass assembly is replaced."],
      ['Avoid full window replacement unless necessary', 'IGU-only replacement is dramatically cheaper and gives functionally identical results.'],
      ['Skip defogging unless on a strict budget', 'Restores appearance but not the argon fill or insulating function.'],
    ],
    avoid: [
      ['Drilling the unit to "release" moisture', 'Turns a $300 IGU replacement into a $1,500 full-window replacement.'],
      ['Caulking over the visible seam', 'Does nothing for the cavity moisture. Compromises the secondary seal further.'],
    ],
    article: { title: "Foggy windows that won't clear: failed-seal diagnosis", slug: 'foggy-windows-failed-seal', num: '006' },
  },
  'white-rain-spots': {
    name: 'Hard Water Deposits from Upstream',
    italic: 'sprinklers, roof runoff, or masonry leaching',
    rating: 'Common · Diagnose the Source',
    summary:
      "Chalky white spots after rain are almost never from the rain itself — rain is distilled water and leaves nothing behind. The deposits come from what the rain washed off your roof, gutters, or upper trim. Sprinkler overspray is the second most common cause. The cleanup is straightforward; finding and stopping the source is the actual job.",
    fix: [
      ['Identify the source first', 'Look up. Check sprinkler aim. Check roof age (new shingles release granule fines). Check masonry above the affected windows.'],
      ['Vinegar at full strength for fresh deposits', 'Soak a cloth, lay against the spots for 5-10 minutes, wipe.'],
      ['Bar Keepers Friend for older deposits', 'The oxalic acid is more effective than vinegar on aged calcium.'],
      ['CLR for badly neglected windows', 'Not for use near stone trim — it will etch limestone or marble surrounds.'],
    ],
    avoid: [
      ['Scrubbing harder with the same cleaner', 'If vinegar does not lighten the spots in 30 seconds, you may have early-stage etching, not removable deposits. Different problem, different fix.'],
      ['Cleaning the windows without addressing the source', "You'll be cleaning the same spots in the same place every month forever."],
    ],
    article: { title: 'White spots on your windows after it rains', slug: 'white-spots-after-rain', num: '005' },
  },
  'stubborn-organic': {
    name: 'Tree Sap, Tar, or Sticky Organic Residue',
    italic: 'wrong chemistry needed — climb the solvent ladder',
    rating: 'Common · Specific Solvents Required',
    summary:
      "Sap, tar, bug residue, and similar sticky deposits are non-polar organic compounds. Water-based cleaners do not touch them — the chemistry is wrong. The fix requires escalating to a non-polar solvent (mineral spirits or naphtha), but only after trying gentler options first to avoid damaging gaskets or coatings.",
    fix: [
      ['Start with hot soapy water and 10-minute dwell', 'Softens fresh sap and lifts surface debris. Often handles 50% of the work for free.'],
      ['Escalate to 91% isopropyl alcohol', 'Handles light sap and most adhesive residue.'],
      ['Mineral spirits or naphtha for stubborn cases', 'Apply to a cloth, not directly to glass. Outdoor or well-ventilated only.'],
      ['Citrus-based degreaser (Goo Gone) for ancient adhesive', 'Only specialty case. Aggressive cleanup afterward to remove the d-limonene oil residue.'],
    ],
    avoid: [
      ['Magic Eraser on coated or laminated glass', 'Micro-abrasive — will scratch through low-E coatings.'],
      ['Acetone', 'Aggressive solvent that damages vinyl trim and silicone gaskets. Naphtha does the same job more safely.'],
      ['WD-40', 'Leaves heavy oily residue that is harder to remove than what you started with.'],
    ],
    article: { title: 'Sap, tar, and bug residue: the solvent ladder', slug: 'solvent-ladder-sap-tar-bugs', num: '011' },
  },
  'gasket-mold': {
    name: 'Gasket Mold (Cladosporium / Aureobasidium)',
    italic: 'common, easily treated, not the same as frame rot',
    rating: 'Common · 20-Minute Fix',
    summary:
      "Black or dark dots on the rubber gasket itself is surface mold, almost always *Cladosporium* or *Aureobasidium* — the same families of mold that grow on shower curtains and refrigerator seals. Not particularly dangerous, but it spreads if untreated. The fix is dilute bleach on the gasket only.",
    fix: [
      ['1:10 bleach-to-water solution in a spray bottle', 'Mist the gasket lightly. Wait 5-10 minutes. Wipe with a clean rag.'],
      ['Hydrogen peroxide (3%) as a safer alternative', 'Slower but identical result. Safer for sensitive users.'],
      ['Address indoor humidity to prevent return', 'Keep indoor humidity under 50%. Wipe gasket dry when condensation appears.'],
    ],
    avoid: [
      ['Mixing bleach with anything else', 'Vinegar + bleach = chlorine gas. Ammonia + bleach = chloramine vapors. Both genuinely dangerous.'],
      ['Bleach on white or colored gaskets without a test patch', 'Can cause yellowing.'],
      ['Painting over the area', 'Mold continues underneath the paint and reappears within months.'],
    ],
    article: { title: 'Black mold around windows: frame vs. glass', slug: 'black-mold-windows-frames-glass', num: '010' },
  },
  'frame-rot': {
    name: 'Wood Frame Moisture Intrusion',
    italic: 'structural problem — call a contractor',
    rating: 'SERIOUS · Contractor Recommended',
    summary:
      "Dark patches on a wooden window frame, especially with soft or punky wood, is not surface mold — it is moisture intrusion damaging the frame structurally. The dark color is a mix of fungal growth, oxidation, and wood decomposition. The visible damage is the symptom; the moisture source is the actual problem to solve.",
    fix: [
      ['Identify the moisture source', 'Failed flashing, leaky frame seal, gutter overflow, condensation issues, water entering from outside the wall.'],
      ['Stop the moisture before treating the visible damage', "Painting over rotted wood without fixing the source spreads the problem."],
      ['Get a contractor to assess structural damage', 'If the wood dents under finger pressure, sections may need replacement. This is not a window cleaning problem.'],
    ],
    avoid: [
      ['Bleach as a "fix"', 'Bleach raises wood grain and damages finishes without addressing the underlying rot.'],
      ['Painting over the damage', 'Hides the symptom, accelerates the structural problem.'],
      ['Ignoring it', 'Frame rot does not stop on its own. Time always makes it worse and more expensive.'],
    ],
    article: { title: 'Black mold around windows: frame vs. glass', slug: 'black-mold-windows-frames-glass', num: '010' },
  },
  'butyl-migration': {
    name: 'Migrated IGU Glazing Tape (Butyl)',
    italic: 'cosmetic — 10-minute fix with mineral spirits',
    rating: 'Common in Older IGUs · Cosmetic',
    summary:
      "The dark band along the perimeter of the glass is migrated butyl from old glazing tape. Common on IGUs from the 1970s through early 2000s. The butyl has softened and migrated outward over time, picking up dust to form the visible stain. The window itself is not failing — this is purely cosmetic, and the fix is gentle and quick.",
    fix: [
      ['Mineral spirits on a clean cloth, applied to the glass-edge stain', 'Non-polar solvent dissolves butyl effectively.'],
      ['Naphtha for stubborn cases', 'Slightly stronger version of the same chemistry.'],
      ['Wipe carefully, keeping solvent off the gasket itself', 'Incidental contact is fine; sustained exposure dulls rubber.'],
      ['Follow with soap-and-water rinse and clean alcohol pass', 'Removes solvent residue.'],
    ],
    avoid: [
      ['Bleach on butyl', 'Does nothing to the residue and damages the rubber gasket.'],
      ['Acidic cleaners (vinegar, CLR)', 'Wrong chemistry. Butyl is non-polar.'],
    ],
    article: { title: 'Black mold around windows: frame vs. glass', slug: 'black-mold-windows-frames-glass', num: '010' },
  },
  'fabricating-debris': {
    name: 'Fabricating Debris Damage (Tempered Glass Defect)',
    italic: 'manufacturing defect — not your fault',
    rating: 'PERMANENT · Restoration or Replacement',
    summary:
      "Many parallel hairline scratches that appeared after a single cleaning, especially with a razor blade, is fabricating debris damage. The tempered glass left the factory with abrasive particles fused to its surface, invisible until a cleaning tool dragged across it. This is a manufacturing defect the glass industry has known about for forty years and quietly refused to fix. The contractor (or you, if you cleaned it) is not at fault.",
    fix: [
      ['Confirm the diagnosis with a 10× loupe', 'Each scratch should have a tiny pit at one end and a thinning tail. The pattern follows your cleaning motion.'],
      ['Replacement for severely damaged panes', '$250-600 installed for residential tempered. Specify a different glass fabricator if possible.'],
      ['Professional polishing for high-value commercial glass', 'Cerium oxide polishing by a glass restoration specialist. $1,500-2,500 for large panes.'],
      ['Living with it for low-visibility damage', 'Hairline scratches do not propagate or compromise structural integrity.'],
    ],
    avoid: [
      ['Cleaning the same glass with a razor blade again', 'More fabricating debris remains on the surface. Damage will continue.'],
      ['Trying to polish with household abrasives', 'Will damage further without resolving the underlying scratches.'],
    ],
    article: { title: 'Scratches after cleaning: fabricating debris', slug: 'scratches-after-cleaning', num: '008' },
  },
  'surface-residue': {
    name: 'Surface Cleaning Residue',
    italic: 'standard residue from over-cleaning',
    rating: 'Common · Quick Fix',
    summary:
      "What you're seeing is the most common cause of windows looking worse after cleaning: cumulative residue from glass cleaners that contain surfactants, dyes, or fragrance compounds. Each cleaning has been depositing slightly more than it removes. The fix is one strip-and-reset cycle, then changing your cleaner protocol.",
    fix: [
      ['Strip with a 50/50 alcohol-and-distilled-water pass', 'Resets the glass to a true clean baseline.'],
      ['Switch to a residue-free protocol going forward', 'Dawn + distilled water at 1 drop per quart, finished with a clean microfiber.'],
      ['Verify with the alcohol test', 'After cleaning, wipe a section with alcohol. If the alcohol-cleaned area looks clearer than the rest, your protocol is leaving residue.'],
    ],
    avoid: [
      ['Switching to a different brand of glass cleaner', 'All consumer glass cleaners contain surfactants. New brand, same residue.'],
      ['Using paper towels for the strip pass', 'They leave fibers and reintroduce contamination.'],
    ],
    article: { title: 'Why your windows look worse after you clean them', slug: 'why-windows-look-worse', num: '001' },
  },
};

// ─── diagnosis logic ──────────────────────────────────────────────────────

function diagnose(answers) {
  const { location, pattern } = answers;

  if (location === 'surface') {
    if (pattern === 'directional') return 'surfactant-hysteresis';
    if (pattern === 'rainbow') return 'outgassing-film';
    if (pattern === 'milky') return 'pressure-wash-haze';
    if (pattern === 'etched') return 'etching';
    return 'surface-residue';
  }

  if (location === 'between') {
    return 'failed-igu-seal';
  }

  if (location === 'spots') {
    if (pattern === 'chalky-white' || pattern === 'gritty') return 'white-rain-spots';
    if (pattern === 'sap-amber') return 'stubborn-organic';
    return 'white-rain-spots';
  }

  if (location === 'edges') {
    if (pattern === 'mold-dots') return 'gasket-mold';
    if (pattern === 'wood-rot') return 'frame-rot';
    if (pattern === 'edge-band') return 'butyl-migration';
    if (pattern === 'pressure-edge') return 'pressure-wash-haze';
    return 'gasket-mold';
  }

  if (location === 'damage') {
    if (pattern === 'comet') return 'fabricating-debris';
    return 'fabricating-debris';
  }

  return 'surface-residue';
}

// ─── component ────────────────────────────────────────────────────────────

export default function StreakDiagnostic() {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [dark, setDark] = useState(false);

  const T = dark
    ? {
        bg: '#1A1D1C', surface: '#22262A', ink: '#F4F1EA',
        inkSoft: '#B8B2A4', inkFaint: '#6B6960', rule: '#2E3236',
        sienna: '#D67148', sage: '#9CA89A',
      }
    : {
        bg: '#F4F1EA', surface: '#FAF7F1', ink: '#1A1D1C',
        inkSoft: '#5A5A52', inkFaint: '#A39E91', rule: '#D9D3C5',
        sienna: '#B5532A', sage: '#9CA89A',
      };

  const fontDisplay = "'Fraunces', 'Times New Roman', serif";
  const fontBody = "'Inter Tight', 'Helvetica Neue', sans-serif";
  const fontMono = "'JetBrains Mono', 'Courier New', monospace";

  // Build the question sequence based on the user's first answer
  const questions = useMemo(() => {
    const list = [Q1];
    if (answers.location && Q2_BRANCHES[answers.location]) {
      list.push(Q2_BRANCHES[answers.location]);
    }
    return list;
  }, [answers.location]);

  const isComplete = answers.location && answers.pattern;
  const verdict = isComplete ? VERDICTS[diagnose(answers)] : null;
  const currentQ = !isComplete ? questions[step] : null;
  const progress = isComplete ? 1 : (step + 1) / 2; // always 2 questions

  function answer(qId, optId) {
    const key = qId === 'location' ? 'location' : 'pattern';
    setAnswers({ ...answers, [key]: optId });
    setStep(step + 1);
  }

  function back() {
    if (step > 0) {
      const newAnswers = { ...answers };
      delete newAnswers.pattern;
      setAnswers(newAnswers);
      setStep(step - 1);
    }
  }

  function reset() {
    setStep(0);
    setAnswers({});
  }

  return (
    <div
      style={{
        minHeight: '100vh', background: T.bg, color: T.ink,
        fontFamily: fontBody,
        transition: 'background 0.3s, color 0.3s',
        padding: '32px 20px 80px',
      }}
    >
      <StaticSeo schemaType="webApplication" path="/tools/streak-diagnostic" />
      <link
        rel="stylesheet"
        href="https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,300;9..144,400;9..144,500;9..144,600&family=Inter+Tight:wght@300;400;500;600&family=JetBrains+Mono:wght@400;500&display=swap"
      />

      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(12px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes slideIn {
          from { opacity: 0; transform: translateX(8px); }
          to   { opacity: 1; transform: translateX(0); }
        }
        .reveal { animation: fadeUp 0.6s cubic-bezier(.2,.7,.3,1) both; }
        .reveal-1 { animation-delay: 0.05s; }
        .reveal-2 { animation-delay: 0.15s; }
        .reveal-3 { animation-delay: 0.25s; }
        .question-enter { animation: fadeUp 0.5s cubic-bezier(.2,.7,.3,1) both; }
        .option-tile { animation: slideIn 0.4s cubic-bezier(.2,.7,.3,1) both; }
        .option-tile:nth-child(1) { animation-delay: 0.05s; }
        .option-tile:nth-child(2) { animation-delay: 0.10s; }
        .option-tile:nth-child(3) { animation-delay: 0.15s; }
        .option-tile:nth-child(4) { animation-delay: 0.20s; }
        .option-tile:nth-child(5) { animation-delay: 0.25s; }
        .visual-tile:hover {
          background: ${T.surface};
          border-color: ${T.sienna} !important;
          transform: translateY(-2px);
        }
        .visual-tile { transition: background 0.2s, border-color 0.2s, transform 0.2s; cursor: pointer; }
        .option-tile:hover {
          background: ${T.surface};
          border-left-color: ${T.sienna} !important;
          transform: translateX(2px);
        }
        .option-tile { transition: background 0.2s, border-color 0.2s, transform 0.2s; cursor: pointer; }
        .ghost-btn:hover { color: ${T.sienna}; }
        .pill-btn:hover { background: ${T.ink}; color: ${T.bg}; }
        .article-link:hover { background: ${T.ink}; color: ${T.bg}; }
        .article-link { transition: background 0.2s, color 0.2s; }

        @media (max-width: 720px) {
          .h1-title { font-size: 36px !important; line-height: 1.05 !important; }
          .q-title { font-size: 26px !important; }
          .visual-grid { grid-template-columns: 1fr 1fr !important; }
        }
      `}</style>

      <div style={{ maxWidth: 880, margin: '0 auto' }}>
        {/* Header */}
        <header
          style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
            paddingBottom: 20, borderBottom: `1px solid ${T.rule}`, marginBottom: 56,
          }}
        >
          <Link
            to="/"
            style={{
              fontFamily: fontDisplay, fontSize: 22, letterSpacing: '-0.01em',
              color: T.ink, textDecoration: 'none',
            }}
          >
            Window Washing <em style={{ fontStyle: 'italic', fontWeight: 400 }}>Guide</em>
          </Link>
          <button
            onClick={() => setDark(!dark)}
            style={{
              background: 'transparent',
              border: `1px solid ${T.rule}`,
              borderRadius: '50%',
              width: 32, height: 32, color: T.inkSoft,
              cursor: 'pointer', fontSize: 13,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            {dark ? '☀' : '☾'}
          </button>
        </header>

        {/* Title */}
        <div className="reveal reveal-1">
          <div
            style={{
              fontFamily: fontMono, fontSize: 11,
              letterSpacing: '0.18em', color: T.sienna,
              marginBottom: 20,
            }}
          >
            § THE TOOLS &nbsp;·&nbsp; 01 / 09 &nbsp;·&nbsp; SIGNATURE &nbsp;·&nbsp; MOST USED
          </div>

          <h1
            className="h1-title"
            style={{
              fontFamily: fontDisplay, fontSize: 56, fontWeight: 400,
              lineHeight: 1.04, letterSpacing: '-0.025em',
              margin: '0 0 24px', maxWidth: 800,
            }}
          >
            The Streak{' '}
            <em style={{ fontStyle: 'italic', color: T.inkSoft, fontWeight: 400 }}>
              Diagnostic
            </em>
            .
          </h1>

          <p
            style={{
              fontFamily: fontDisplay, fontSize: 21, fontWeight: 300,
              lineHeight: 1.5, color: T.inkSoft,
              maxWidth: 620, margin: '0 0 12px',
            }}
          >
            Two questions, one verdict. Identify what is actually wrong with
            your window — and find the article that walks you through the fix.
          </p>

          <p
            style={{
              fontFamily: fontMono, fontSize: 11,
              letterSpacing: '0.05em', color: T.inkFaint, marginBottom: 56,
            }}
          >
            Maps to twelve diagnoses across the Diagnostician pillar.
          </p>
        </div>

        {/* Progress */}
        {!isComplete && (
          <div className="reveal reveal-2" style={{ marginBottom: 40 }}>
            <div
              style={{
                display: 'flex', justifyContent: 'space-between',
                fontFamily: fontMono, fontSize: 10,
                letterSpacing: '0.18em', color: T.inkFaint,
                marginBottom: 10,
              }}
            >
              <span>QUESTION {step + 1} OF 2</span>
              <span>{Math.round(progress * 100)}% COMPLETE</span>
            </div>
            <div style={{ height: 1, background: T.rule, position: 'relative' }}>
              <div
                style={{
                  position: 'absolute', top: -1, left: 0,
                  height: 3, background: T.sienna,
                  width: `${progress * 100}%`,
                  transition: 'width 0.4s cubic-bezier(.2,.7,.3,1)',
                }}
              />
            </div>
          </div>
        )}

        {/* Question */}
        {!isComplete && currentQ && (
          <div key={currentQ.id} className="question-enter">
            <h2
              className="q-title"
              style={{
                fontFamily: fontDisplay, fontSize: 36, fontWeight: 400,
                lineHeight: 1.1, letterSpacing: '-0.02em',
                margin: '0 0 8px',
              }}
            >
              {currentQ.title}
            </h2>
            <p
              style={{
                fontFamily: fontDisplay, fontStyle: 'italic',
                fontSize: 17, fontWeight: 300, color: T.inkSoft,
                margin: '0 0 24px', lineHeight: 1.4,
              }}
            >
              {currentQ.italic}
            </p>
            <p
              style={{
                fontSize: 15, fontWeight: 300, color: T.inkSoft,
                lineHeight: 1.55, margin: '0 0 36px',
                maxWidth: 720,
                paddingLeft: 14, borderLeft: `2px solid ${T.rule}`,
              }}
            >
              {currentQ.note}
            </p>

            {/* Options — visual grid for Q2, plain list for Q1 */}
            {currentQ.options[0]?.Visual ? (
              <div
                className="visual-grid"
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  gap: 16,
                }}
              >
                {currentQ.options.map((opt) => {
                  const Visual = opt.Visual;
                  return (
                    <div
                      key={opt.id}
                      className="visual-tile option-tile"
                      onClick={() => answer(currentQ.id, opt.id)}
                      style={{
                        border: `1px solid ${T.rule}`,
                        background: T.bg,
                        padding: 14,
                      }}
                    >
                      <div
                        style={{
                          width: '100%',
                          aspectRatio: '200/140',
                          marginBottom: 14,
                          background: T.surface,
                          overflow: 'hidden',
                        }}
                      >
                        <Visual T={T} />
                      </div>
                      <div
                        style={{
                          fontFamily: fontDisplay, fontSize: 17,
                          fontWeight: 500, lineHeight: 1.25,
                          marginBottom: 6, color: T.ink,
                        }}
                      >
                        {opt.label}
                      </div>
                      <div
                        style={{
                          fontSize: 13, fontWeight: 300,
                          lineHeight: 1.45, color: T.inkSoft,
                        }}
                      >
                        {opt.desc}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                {currentQ.options.map((opt) => (
                  <div
                    key={opt.id}
                    className="option-tile"
                    onClick={() => answer(currentQ.id, opt.id)}
                    style={{
                      borderLeft: `3px solid ${T.rule}`,
                      paddingLeft: 20,
                      paddingTop: 18, paddingBottom: 18,
                      paddingRight: 14,
                      borderBottom: `1px solid ${T.rule}`,
                    }}
                  >
                    <div
                      style={{
                        fontFamily: fontDisplay, fontSize: 19,
                        fontWeight: 500, lineHeight: 1.25,
                        marginBottom: 5, color: T.ink,
                      }}
                    >
                      {opt.label}
                    </div>
                    <div
                      style={{
                        fontSize: 14, fontWeight: 300,
                        lineHeight: 1.45, color: T.inkSoft,
                      }}
                    >
                      {opt.desc}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {step > 0 && (
              <button
                onClick={back}
                className="ghost-btn"
                style={{
                  marginTop: 32,
                  background: 'transparent', border: 'none',
                  color: T.inkSoft, cursor: 'pointer',
                  fontFamily: fontMono, fontSize: 11,
                  letterSpacing: '0.16em',
                  padding: 0,
                  transition: 'color 0.2s',
                }}
              >
                ← BACK
              </button>
            )}
          </div>
        )}

        {/* Verdict */}
        {isComplete && verdict && (
          <div className="reveal reveal-1">
            <div
              style={{
                fontFamily: fontMono, fontSize: 11,
                letterSpacing: '0.18em', color: T.sienna,
                marginBottom: 16,
              }}
            >
              ◆ DIAGNOSIS &nbsp;·&nbsp; {verdict.rating}
            </div>
            <h2
              style={{
                fontFamily: fontDisplay, fontSize: 44, fontWeight: 400,
                lineHeight: 1.05, letterSpacing: '-0.025em',
                margin: '0 0 8px',
              }}
            >
              {verdict.name}
              <span style={{ color: T.sienna }}>.</span>
            </h2>
            <p
              style={{
                fontFamily: fontDisplay, fontStyle: 'italic',
                fontSize: 19, fontWeight: 300, color: T.inkSoft,
                margin: '0 0 32px', lineHeight: 1.4,
              }}
            >
              {verdict.italic}
            </p>

            <p
              style={{
                fontSize: 17, fontWeight: 300, lineHeight: 1.6,
                color: T.ink, marginBottom: 36,
                paddingLeft: 18, borderLeft: `3px solid ${T.sienna}`,
              }}
            >
              {verdict.summary}
            </p>

            {/* Fix */}
            <div style={{ marginBottom: 36 }}>
              <div
                style={{
                  fontFamily: fontMono, fontSize: 11,
                  letterSpacing: '0.18em', color: T.sage,
                  marginBottom: 14,
                }}
              >
                ✓ THE FIX
              </div>
              <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                {verdict.fix.map(([title, detail], i) => (
                  <li
                    key={i}
                    style={{
                      paddingTop: 14, paddingBottom: 14,
                      borderBottom: `1px solid ${T.rule}`,
                    }}
                  >
                    <div
                      style={{
                        fontFamily: fontDisplay, fontSize: 17,
                        fontWeight: 500, marginBottom: 4,
                      }}
                    >
                      {title}
                    </div>
                    <div
                      style={{
                        fontSize: 14, fontWeight: 300,
                        color: T.inkSoft, lineHeight: 1.5,
                      }}
                    >
                      {detail}
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            {/* Avoid */}
            <div style={{ marginBottom: 40 }}>
              <div
                style={{
                  fontFamily: fontMono, fontSize: 11,
                  letterSpacing: '0.18em', color: T.sienna,
                  marginBottom: 14,
                }}
              >
                ✗ AVOID
              </div>
              <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                {verdict.avoid.map(([title, detail], i) => (
                  <li
                    key={i}
                    style={{
                      paddingTop: 14, paddingBottom: 14,
                      borderBottom: `1px solid ${T.rule}`,
                    }}
                  >
                    <div
                      style={{
                        fontFamily: fontDisplay, fontSize: 17,
                        fontWeight: 500, marginBottom: 4,
                      }}
                    >
                      {title}
                    </div>
                    <div
                      style={{
                        fontSize: 14, fontWeight: 300,
                        color: T.inkSoft, lineHeight: 1.5,
                      }}
                    >
                      {detail}
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            {/* Article deep-link */}
            <Link
              to={`/articles/${verdict.article.slug}`}
              className="article-link"
              style={{
                display: 'block',
                padding: '24px 24px',
                border: `1px solid ${T.rule}`,
                background: T.surface,
                color: T.ink, textDecoration: 'none',
                marginBottom: 32,
              }}
            >
              <div
                style={{
                  fontFamily: fontMono, fontSize: 11,
                  letterSpacing: '0.18em', color: T.sienna,
                  marginBottom: 8,
                }}
              >
                FULL DEEP-DIVE &nbsp;·&nbsp; ARTICLE № {verdict.article.num}
              </div>
              <div
                style={{
                  fontFamily: fontDisplay, fontSize: 22,
                  fontWeight: 400, lineHeight: 1.2,
                  letterSpacing: '-0.01em',
                }}
              >
                {verdict.article.title} →
              </div>
            </Link>

            {/* Reset */}
            <button
              onClick={reset}
              className="pill-btn"
              style={{
                background: 'transparent',
                border: `1px solid ${T.ink}`,
                padding: '12px 28px',
                color: T.ink, cursor: 'pointer',
                fontFamily: fontMono, fontSize: 11,
                letterSpacing: '0.18em',
                transition: 'background 0.2s, color 0.2s',
              }}
            >
              ⟲ RUN ANOTHER DIAGNOSIS
            </button>
          </div>
        )}

        {/* Footer */}
        <footer
          style={{
            marginTop: 80, paddingTop: 24,
            borderTop: `1px solid ${T.rule}`,
            display: 'flex', justifyContent: 'space-between',
            alignItems: 'baseline', flexWrap: 'wrap', gap: 16,
            fontFamily: fontMono, fontSize: 10,
            letterSpacing: '0.16em', color: T.inkFaint,
          }}
        >
          <span>TOOL 01 / 09 · STREAK DIAGNOSTIC</span>
          <Link
            to="/tools/streak-diagnostic/guide"
            style={{
              color: T.sienna,
              textDecoration: 'none',
              fontFamily: fontMono, fontSize: 10,
              letterSpacing: '0.16em',
            }}
          >
            READ THE METHODOLOGY →
          </Link>
        </footer>
      </div>
    </div>
  );
}
